// client/src/coms/auth/providerAuth.js
// Opening external/provider-login URLs and receiving provider OAuth callbacks.
//
// Provider redirect URIs use the app's custom scheme (voxemera-beta://<provider>/callback). The
// desktop app (voxemera-tauri, src-tauri/src/deeplink.rs) receives that deep link, navigates the
// main window to /comms/#desktop-auth-callback=<base64url>, and its initialization script decodes
// it, stores it under AUTH_CALLBACK_STORAGE_KEY / window.__VOX_PROVIDER_AUTH_CALLBACK and
// dispatches AUTH_CALLBACK_EVENT_NAME. This module consumes those payloads.
//
// Uses the global Tauri API (withGlobalTauri) rather than @tauri-apps/plugin-opener, which is not
// a dependency of this client.

const AUTH_CALLBACK_EVENT_NAME = 'vox-provider-auth-callback';
const AUTH_CALLBACK_STORAGE_KEY = '__vox_provider_auth_callback__';
const AUTH_CALLBACK_HASH_KEY = 'desktop-auth-callback';
const PENDING_CALLBACK_MAX_AGE_MS = 10 * 60 * 1000;

function safeJsonParse(value, fallback = null) {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

export function isTauriApp() {
  try {
    return !!(window && window.__TAURI__);
  } catch {
    return false;
  }
}

function getTauriOpener() {
  try {
    const opener = window.__TAURI__ && window.__TAURI__.opener;
    return opener && typeof opener.openUrl === 'function' ? opener : null;
  } catch {
    return null;
  }
}

function isAllowedExternalUrl(value) {
  try {
    const url = new URL(String(value || ''), window.location.href);
    return url.protocol === 'https:' || url.protocol === 'http:' || url.protocol === 'mailto:';
  } catch {
    return false;
  }
}

export async function openExternalUrl(url) {
  const nextUrl = String(url || '').trim();
  if (!nextUrl || !isAllowedExternalUrl(nextUrl)) return false;

  if (isTauriApp()) {
    const opener = getTauriOpener();
    if (opener) {
      try {
        await opener.openUrl(nextUrl);
        return true;
      } catch {}
    }
  }

  try {
    // With noopener, browsers return null even when the tab opened; only a throw means failure.
    window.open(nextUrl, '_blank', 'noopener,noreferrer');
    return true;
  } catch {}

  try {
    window.location.assign(nextUrl);
    return true;
  } catch {}

  return false;
}

function clearPendingProviderAuthCallback() {
  try {
    localStorage.removeItem(AUTH_CALLBACK_STORAGE_KEY);
  } catch {}
  try {
    window.__VOX_PROVIDER_AUTH_CALLBACK = null;
  } catch {}
}

// Opens a provider authorisation page. In the desktop app it goes to the system browser so the
// provider can redirect back through the custom-scheme deep link; in a browser it opens a new tab.
export async function openAuthUrl(url, { provider } = {}) {
  const nextUrl = String(url || '').trim();
  if (!nextUrl) return false;

  try {
    if (new URL(nextUrl).protocol !== 'https:') return false;
  } catch {
    return false;
  }

  // A stale callback from an earlier attempt must not be replayed into this one.
  clearPendingProviderAuthCallback();

  try {
    if (provider) window.__VOX_PROVIDER_AUTH_IN_FLIGHT = { provider: String(provider), startedAt: Date.now() };
  } catch {}

  return openExternalUrl(nextUrl);
}

function isFreshPayload(payload) {
  if (!payload || typeof payload !== 'object' || !payload.url) return false;
  const receivedAt = Number(payload.receivedAt) || 0;
  return !receivedAt || Date.now() - receivedAt <= PENDING_CALLBACK_MAX_AGE_MS;
}

// Fallback for when the desktop initialization script did not run for this page load: decode
// #desktop-auth-callback=<base64url> here and remove it from the address bar.
function readHashAuthCallback() {
  try {
    const hash = String(window.location.hash || '').replace(/^#/, '');
    if (!hash) return null;
    const params = new URLSearchParams(hash);
    const encoded = String(params.get(AUTH_CALLBACK_HASH_KEY) || '').trim();
    if (!encoded) return null;

    const normalized = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const url = atob(padded);

    try {
      const clean = new URL(window.location.href);
      clean.hash = '';
      window.history.replaceState({}, document.title, clean.toString());
    } catch {}

    return url ? { url, receivedAt: Date.now(), source: 'desktop_hash' } : null;
  } catch {
    return null;
  }
}

export function consumePendingProviderAuthCallback() {
  let payload = null;

  try {
    const raw = localStorage.getItem(AUTH_CALLBACK_STORAGE_KEY);
    if (raw) payload = safeJsonParse(raw, null);
  } catch {}

  if (!isFreshPayload(payload)) {
    try {
      payload = window.__VOX_PROVIDER_AUTH_CALLBACK || null;
    } catch {
      payload = null;
    }
  }

  if (!isFreshPayload(payload)) payload = readHashAuthCallback();

  clearPendingProviderAuthCallback();
  return isFreshPayload(payload) ? payload : null;
}

export function subscribeProviderAuthCallbacks(listener, { consumePending = true } = {}) {
  if (typeof listener !== 'function') return () => {};

  if (consumePending) {
    const pending = consumePendingProviderAuthCallback();
    if (pending) {
      try {
        listener(pending);
      } catch {}
    }
  }

  const handler = (event) => {
    const detail = event && event.detail && typeof event.detail === 'object' ? event.detail : null;
    if (!detail || !detail.url) return;

    clearPendingProviderAuthCallback();

    try {
      listener(detail);
    } catch {}
  };

  window.addEventListener(AUTH_CALLBACK_EVENT_NAME, handler);
  return () => window.removeEventListener(AUTH_CALLBACK_EVENT_NAME, handler);
}
